"use client";

import type { ReactNode } from "react";
import { Phone, Search } from "lucide-react";
import { formatLebanesePhone } from "@/modules/identity/surfaces";
import type { BusinessCustomer } from "@/modules/insights/surfaces";
import { DataUnavailable } from "./DataUnavailable";

/**
 * CustomersScreen (docs/ux/screens/CustomersScreen.md, H27), shared by both
 * frontends (R22 T14).
 *
 * The Consumers who have booked with this clinic, searchable by name or phone.
 * Read-only, and open to **both** tiers (H26): a Practitioner defaults to their
 * own customers with a toggle to All, a Manager sees all. Scoped to the
 * caller's clinic by the session, never by anything a client sends.
 *
 * **The name and the phone are two different targets on purpose.** The name
 * opens the Consumer File (H42), which is what staff wrote about somebody; the
 * phone is a `tel:` link. Ringing a customer should not mean opening their
 * file, and the two intentions deserve two controls.
 *
 * The phone is formatted the way it is everywhere else in the app, through
 * Identity's shared formatter, because the reason a clinic opens this screen is
 * almost always to ring somebody and a number grouped differently here would
 * read as a different number.
 */
export type CustomersLink = (props: {
  href: string;
  className?: string;
  "aria-current"?: "true" | undefined;
  children: ReactNode;
}) => ReactNode;

export function CustomersList({
  customers,
  query,
  scope,
  hasOwn,
  failed,
  configured = true,
  Link,
  onSearch,
}: {
  /** Null while the list could not be read at all. */
  customers: BusinessCustomer[] | null;
  query: string;
  scope: "mine" | "all";
  /** Whether this caller holds a seat, and so has a "mine" to scope to. */
  hasOwn: boolean;
  failed: boolean;
  configured?: boolean;
  Link: CustomersLink;
  /** Submit the search. The host decides whether that is a navigation. */
  onSearch?: (event: React.FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <>
      <header className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-ink">Customers</h1>
        <p className="mt-1 text-sm text-muted">
          Everyone who has booked with you, most recent first.
        </p>
      </header>

      <form
        action="/business/customers"
        method="get"
        onSubmit={onSearch}
        className="mb-4"
      >
        {hasOwn && <input type="hidden" name="scope" value={scope} />}
        <div className="relative">
          <Search
            className="pointer-events-none absolute start-4 top-1/2 size-4.5 -translate-y-1/2 text-muted"
            aria-hidden
          />
          <input
            type="search"
            name="q"
            defaultValue={query}
            placeholder="Search by name or phone"
            aria-label="Search by name or phone"
            className="h-12 w-full rounded-xl border border-line bg-surface pe-4 ps-11 text-body outline-none transition placeholder:text-muted focus-visible:border-accent"
          />
        </div>
      </form>

      {hasOwn && <ScopeToggle scope={scope} q={query} Link={Link} />}

      {failed || customers === null ? (
        <DataUnavailable configured={configured} what="your customers" />
      ) : customers.length === 0 ? (
        <p className="rounded-2xl border border-line bg-surface p-10 text-center text-sm text-muted">
          {query.trim() !== ""
            ? "No customer matches that name or phone."
            : scope === "mine"
              ? "No customers yet. People you see will appear here."
              : "No customers yet. People who book with you will appear here."}
        </p>
      ) : (
        <ul className="space-y-3">
          {customers.map((customer) => (
            <CustomerRow key={customer.id} customer={customer} Link={Link} />
          ))}
        </ul>
      )}
    </>
  );
}

function ScopeToggle({
  scope,
  q,
  Link,
}: {
  scope: "mine" | "all";
  q: string;
  Link: CustomersLink;
}) {
  const options: { key: "mine" | "all"; label: string }[] = [
    { key: "mine", label: "My customers" },
    { key: "all", label: "All" },
  ];
  const query = (s: string) => {
    const parts = [`scope=${s}`];
    if (q.trim() !== "") parts.push(`q=${encodeURIComponent(q)}`);
    return `/business/customers?${parts.join("&")}`;
  };
  return (
    <div className="mb-4 inline-flex rounded-lg border border-line bg-surface p-0.5 text-sm">
      {options.map((option) => {
        const isActive = option.key === scope;
        return (
          <Link
            key={option.key}
            href={query(option.key)}
            aria-current={isActive ? "true" : undefined}
            className={`rounded-md px-3 py-1.5 font-semibold transition-colors ${
              isActive ? "bg-primary text-on-primary" : "text-muted hover:text-ink"
            }`}
          >
            {option.label}
          </Link>
        );
      })}
    </div>
  );
}

function CustomerRow({
  customer,
  Link,
}: {
  customer: BusinessCustomer;
  Link: CustomersLink;
}) {
  const lastVisit = customer.lastVisit
    ? new Date(customer.lastVisit).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : null;

  return (
    <li className="rounded-2xl border border-line bg-surface p-5 shadow-[var(--shadow-card)]">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          {/* The name opens the file (H42); the phone stays its own control, so
              ringing somebody does not mean opening what staff wrote about
              them. Two different intentions, two different targets. */}
          <Link
            href={`/business/customers/${customer.id}`}
            className="font-semibold text-ink hover:text-accent hover:underline"
          >
            {customer.name}
          </Link>
          {customer.phone ? (
            <a
              href={`tel:${customer.phone}`}
              className="mt-1 inline-flex items-center gap-1.5 font-mono text-sm font-medium text-accent hover:underline"
            >
              <Phone className="size-3.5 shrink-0" aria-hidden strokeWidth={1.8} />
              {formatLebanesePhone(customer.phone)}
            </a>
          ) : (
            <p className="mt-1 text-sm text-muted">No phone on file</p>
          )}
        </div>
        <div className="shrink-0 text-end">
          <p className="font-mono text-sm font-semibold text-ink">
            {customer.bookingCount}
          </p>
          <p className="text-xs text-muted">
            {customer.bookingCount === 1 ? "booking" : "bookings"}
          </p>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3 border-t border-line pt-3 text-xs">
        <span className="text-muted">{lastVisit ? `Last seen ${lastVisit}` : ""}</span>
        <Link
          href={`/business/customers/${customer.id}`}
          className="shrink-0 font-semibold text-accent hover:underline"
        >
          Open file
        </Link>
      </div>
    </li>
  );
}
